import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=loading, false=unauthenticated, object=authenticated
  const [residentUser, setResidentUser] = useState(null);
  const [residentLoading, setResidentLoading] = useState(true);

  useEffect(() => { checkAuth(); checkResidentAuth(); }, []);

  const checkAuth = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(false);
    }
  };

  // Check if resident cookie is still valid (survives page refresh)
  const checkResidentAuth = async () => {
    try {
      const { data } = await api.get('/resident/me');
      setResidentUser(data);
    } catch {
      setResidentUser(null);
    } finally {
      setResidentLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(false);
    setResidentUser(null);
  };

  const residentLogin = async (email, pin) => {
    await api.post('/auth/resident/dev-login', { email, pin });
    // Normalise: fetch canonical resident data so all fields (display_name, etc.) are consistent
    const { data: meData } = await api.get('/resident/me');
    setResidentUser(meData);
    return meData;
  };

  // H1: OTP Authentication methods
  const requestOtp = async (mobile, hintCountry = null) => {
    const payload = { mobile };
    if (hintCountry) payload.hint_country = hintCountry;
    const { data } = await api.post('/auth/resident/request-otp', payload);
    return data;
  };

  const verifyOtp = async (mobile, code, hintCountry = null) => {
    const payload = { mobile, code };
    if (hintCountry) payload.hint_country = hintCountry;
    await api.post('/auth/resident/verify-otp', payload);
    // Normalise: fetch canonical resident data so all fields are consistent
    const { data: meData } = await api.get('/resident/me');
    setResidentUser(meData);
    return meData;
  };

  const residentLogout = () => setResidentUser(null);

  const isPlatformAdmin = user && user.role === 'platform_admin';
  const isCommunityAdmin = user && (user.role === 'community_admin' || user.role === 'platform_admin');
  const isAdmin = user && ['platform_admin', 'community_admin', 'community_staff'].includes(user.role);

  return (
    <AuthContext.Provider value={{
      user, residentUser, residentLoading, login, logout,
      residentLogin, residentLogout, requestOtp, verifyOtp,
      checkAuth, checkResidentAuth, isPlatformAdmin, isCommunityAdmin, isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
